import { Base } from "@app/controllers/base";

export default class extends Base {
  static targets = [
    "star",
    "input",
    "comment",
    "statusText",
    "anonymous",
    "submitButton",
  ]

  static values = {
    bookingCode: String,
  }

  static colorClasses = {
    1: ["text-gold", "brightness-75"],
    2: ["text-gold", "brightness-90"],
    3: ["text-gold", "brightness-100"],
    4: ["text-gold", "brightness-110"],
    5: ["text-gold", "brightness-125", "drop-shadow-sm"],
  }

  static defaultClasses = ["text-slate-200", "drop-shadow-none", "brightness-100"]

  static allColorTokens = [
    ...new Set([
      ...Object.values(this.colorClasses ?? {}).flat?.() ?? [],
    ])
  ]

  static statusTexts = {
    1: "Very Poor 😞",
    2: "Below Expectations 😕",
    3: "Satisfactory 🙂",
    4: "Excellent 😍",
    5: "Best of the Best! 🚀"
  }

  connect() {
    this.storageKey = `review_draft_${this.bookingCodeValue}`
    this.loadDraft()
    this.updateStars(this.rating)
    this.toggleSubmitButton()
  }

  hover(event) {
    const value = parseInt(event.currentTarget.dataset.value)
    this.updateStars(value)
  }

  leave() {
    this.updateStars(this.rating)
  }

  set(event) {
    this.rating = parseInt(event.currentTarget.dataset.value)
    this.inputTarget.value = this.rating
    this.updateStars(this.rating)
    this.toggleSubmitButton()
    this.saveDraft()

    // Add pop animation to the clicked star for mobile tactile feedback
    const starBtn = event.currentTarget
    starBtn.classList.add("check-pop")
    setTimeout(() => {
      starBtn.classList.remove("check-pop")
    }, 400)
  }

  toggleSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !this.rating || this.rating < 1
    }
  }

  saveDraft() {
    const draft = {
      rating: this.rating,
      comment: this.hasCommentTarget ? this.commentTarget.value : "",
      anonymous: this.hasAnonymousTarget ? this.anonymousTarget.checked : false,
    }
    sessionStorage.setItem(this.storageKey, JSON.stringify(draft))
  }

  loadDraft() {
    this.rating = parseInt(this.inputTarget.value) || 0

    const draftData = sessionStorage.getItem(this.storageKey)
    if (draftData) {
      try {
        const draft = JSON.parse(draftData)
        if (draft.rating) {
          this.rating = draft.rating
          this.inputTarget.value = draft.rating
        }
        if (draft.comment && this.hasCommentTarget) {
          this.commentTarget.value = draft.comment
        }
        if (this.hasAnonymousTarget) {
          this.anonymousTarget.checked = !!draft.anonymous
        }
      } catch (e) {
        console.error("Failed to parse review draft", e)
      }
    }
  }

  updateStars(value) {
    const colorClasses = this.constructor.colorClasses
    const defaultClasses = this.constructor.defaultClasses
    const allColorTokens = [
      ...new Set(Object.values(colorClasses).flat())
    ]

    this.starTargets.forEach((star) => {
      const starValue = parseInt(star.dataset.value)

      star.classList.remove(...allColorTokens, ...defaultClasses)

      if (starValue <= value) {
        star.classList.add(...colorClasses[value])
      } else {
        star.classList.add(...defaultClasses)
      }
    })

    if (this.hasStatusTextTarget) {
      const statusTexts = this.constructor.statusTexts

      if (value > 0) {
        this.statusTextTarget.textContent = statusTexts[value]

        // First remove the old color, then add the new one—don’t overwrite it
        this.statusTextTarget.classList.remove(...allColorTokens.filter(t => t.startsWith("text-")))
        this.statusTextTarget.classList.add(colorClasses[value][0])
      } else {
        this.statusTextTarget.textContent = ""
        this.statusTextTarget.classList.remove(...allColorTokens.filter(t => t.startsWith("text-")))
      }
    }
  }

  // Clear the review draft
  clearDraft() {
    sessionStorage.removeItem(this.storageKey)
  }
}
